import { Skeleton } from "@mui/material";
import Grid from "@mui/material/Grid";
import { useContext } from "react";
import { ConstantContext } from "components/context/ConstantProvider";
import numberFormatter from "components/shared/numberFormatter";
import { EditButton } from "components/ui/atoms/Button";
import { ViewCheckbox } from "components/ui/atoms/Checkbox";
import {
  LabelValueForTags,
  LabelValueForText,
} from "components/ui/atoms/LabelValue";
import SectionGrid from "components/ui/atoms/SectionGrid";
import {
  ColorTag,
  InvestmentStatusTag,
  PersonTag,
  TeamSourceTag,
} from "components/ui/atoms/Tag";
import TypographyWithMiss from "components/ui/atoms/TypographyWithMiss";
import { useResetDealFlowCompanyState } from "models/companies/apiCompany";
import { useDeal } from "models/deals/apiDeal";
import { useModal } from "@ebay/nice-modal-react";

const DealInfo = ({ dealId }) => {
  const { data: deal, isLoading, isError } = useDeal(dealId);
  const { dealSources, dealWomanAmongFounders } = useContext(ConstantContext);
  const resetDealFlowCompanyState = useResetDealFlowCompanyState();
  const dealModal = useModal("DealDrawer");

  if (isLoading) return <Skeleton variant="rectangular" height={200} />;
  if (isError) return <>Error</>;

  return (
    <>
      <Grid container spacing={1}>
        <EditButton
          onClick={() =>
            dealModal
              .show({
                id: deal.id,
                formType: "update",
              })
              .then(() => resetDealFlowCompanyState(deal.company))
          }
        />
        <SectionGrid title="Information" size={{ xs: 12, md: 6 }}>
          <LabelValueForText
            label="Owner"
            value={deal.deal_owner_detail?.fullname}
            isMissing={
              deal.deal_owner_detail?.fullname?.trim() === "" ||
              deal.deal_owner_detail === null
            }
            sx={{ mb: 0.5 }}
          />
          <LabelValueForText
            label="Secondary owner"
            value={deal.secondary_owner_detail?.fullname}
            isMissing={
              deal.secondary_owner_detail?.fullname?.trim() === "" ||
              deal.secondary_owner_detail?.fullname === null
            }
            sx={{ mb: 0.5 }}
          />
          <LabelValueForTags
            label="Investment status"
            Tags={[<InvestmentStatusTag status={deal.investment_status} />]}
            sx={{ mb: 0.5 }}
          />
          <SectionGrid title="Comment" size={12}>
            <TypographyWithMiss
              variant="body2"
              whiteSpace="pre-line"
              isMissing={deal.comment?.trim() === "" || deal.comment === null}
            >
              {deal.comment}
            </TypographyWithMiss>
          </SectionGrid>
        </SectionGrid>
        <SectionGrid title="Classification" size={{ xs: 12, md: 6 }}>
          <LabelValueForText
            label="Profitable"
            value={<ViewCheckbox value={deal.profitable} />}
            isMissing={deal.profitable === null}
            sx={{ mb: 0.5 }}
          />
          <LabelValueForText
            label="Majority owned by founders"
            value={<ViewCheckbox value={deal.majority_owned_by_founders} />}
            isMissing={deal.majority_owned_by_founders === null}
            sx={{ mb: 0.5 }}
          />
          <LabelValueForText
            label="Has fundraiser"
            value={<ViewCheckbox value={deal.has_fundraiser} />}
            isMissing={deal.has_fundraiser === null}
            sx={{ mb: 0.5 }}
          />
          <LabelValueForText
            label="Fundraiser"
            value={deal.intermediary}
            isMissing={
              deal.intermediary?.trim() === "" || deal.intermediary === null
            }
            sx={{ mb: 0.5 }}
          />
          <LabelValueForText
            label="Investment round"
            value={deal.investment_round}
            isMissing={
              deal.investment_round?.trim() === "" ||
              deal.investment_round === null
            }
            sx={{ mb: 0.5 }}
          />
          <LabelValueForText
            label="Requested amount"
            value={`${numberFormatter(deal.requested_amount || "")} €`}
            isMissing={deal.requested_amount === null}
            sx={{ mb: 0.5 }}
          />
          <LabelValueForText
            label="Reference revenue"
            value={`${numberFormatter(deal.reference_revenue || "")} €`}
            isMissing={deal.reference_revenue == null}
            sx={{ mb: 0.5 }}
          />
          <LabelValueForText
            label="Reference revenue year"
            value={deal.reference_revenue_year}
            isMissing={deal.reference_revenue_year === null}
            sx={{ mb: 0.5 }}
          />
          <LabelValueForText
            label="Woman among founders"
            value={
              dealWomanAmongFounders.filter((status) => {
                return deal.woman_among_founders == status.value;
              })[0]?.label
            }
            isMissing={
              deal.woman_among_founders.trim() === "" ||
              deal.woman_among_founders === null
            }
            sx={{ mb: 0.5 }}
          />
        </SectionGrid>
        <SectionGrid title="Reason to close" size={6}>
          <TypographyWithMiss
            variant="body2"
            whiteSpace="pre-line"
            isMissing={
              deal.reason_to_close?.trim() === "" ||
              deal.reason_to_close === null
            }
          >
            {deal.reason_to_close}
          </TypographyWithMiss>
          {deal.reason_to_close_tag.length > 0 && (
            <LabelValueForTags
              Tags={deal.reason_to_close_tag.map((tagId) => (
                <ColorTag key={tagId} tagId={tagId} />
              ))}
              sx={{ py: 1, mb: 0.5 }}
            />
          )}
        </SectionGrid>
        <SectionGrid title="Source" size={6}>
          <LabelValueForTags
            label="Team sources"
            Tags={deal.team_sources.map((team_source) => (
              <TeamSourceTag teamSource={team_source} />
            ))}
            sx={{ mb: 0.5 }}
          />
          <LabelValueForText
            label="Deal source"
            value={
              dealSources.filter((source) => {
                return deal.deal_source == source.value;
              })[0]?.label
            }
            isMissing={
              deal.deal_source?.trim() === "" || deal.deal_source === null
            }
            sx={{ mb: 0.5 }}
          />
          <LabelValueForTags
            label="Contributors"
            Tags={deal.contributor_people_detail.map((person) => (
              <PersonTag person={person} />
            ))}
            isMissing={deal.contributor_people_detail?.length == 0}
            sx={{ mb: 0.5 }}
          />
        </SectionGrid>
      </Grid>
    </>
  );
};

export default DealInfo;
